import React, { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faShieldAlt,
  faTimes,
  faRotate,
  faSpinner,
} from '@fortawesome/free-solid-svg-icons';
import { useRisk } from '../contexts/RiskContext';

const formatMoney = (value) => {
  if (value == null || Number.isNaN(Number(value))) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 2,
  }).format(Number(value));
};

const RiskPanel = ({ open: openProp, onClose, hideTrigger = false }) => {
  const { settings, updateSettings, account, accountLoading, refreshAccount, dayPl } = useRisk();
  const [internalOpen, setInternalOpen] = useState(false);
  const controlled = openProp !== undefined;
  const open = controlled ? openProp : internalOpen;
  const setOpen = (next) => {
    if (!controlled) setInternalOpen(next);
    if (!next) onClose?.();
  };

  const lossRoom = useMemo(() => {
    if (settings.dailyLossLimitUsd == null || dayPl == null) return null;
    return settings.dailyLossLimitUsd + dayPl;
  }, [settings.dailyLossLimitUsd, dayPl]);

  const dayPlTone =
    dayPl == null ? 'text-muted' : dayPl >= 0 ? 'text-bull' : 'text-bear';

  const limitBreached =
    settings.enabled &&
    settings.dailyLossLimitUsd != null &&
    dayPl != null &&
    dayPl <= -Math.abs(settings.dailyLossLimitUsd);

  return (
    <>
      {!hideTrigger && (
        <button
          type="button"
          onClick={() => {
            if (controlled) onClose?.(); // parent should open; unused when hideTrigger
            else setInternalOpen(true);
          }}
          className="h-11 px-3 rounded-md border border-hairline bg-surface text-ink hover:bg-elevated text-xs font-semibold inline-flex items-center gap-2 shadow-[0_8px_24px_rgba(0,0,0,0.28)]"
          title="Risk controls"
        >
          <FontAwesomeIcon
            icon={faShieldAlt}
            className={limitBreached ? 'text-bear' : 'text-action'}
          />
          Risk
          {limitBreached && (
            <span className="num px-1.5 py-0.5 rounded bg-bear text-white text-[10px]">LIMIT</span>
          )}
          {!settings.enabled && (
            <span className="num px-1.5 py-0.5 rounded bg-elevated text-muted text-[10px]">OFF</span>
          )}
        </button>
      )}

      {open &&
        createPortal(
          <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 overflow-y-auto">
            <button
              type="button"
              className="fixed inset-0 bg-[#0B0E14]/70"
              aria-label="Close risk panel"
              onClick={() => setOpen(false)}
            />
            <div className="relative z-[1] w-full sm:max-w-[480px] max-h-[min(88dvh,880px)] overflow-y-auto rounded-xl border border-hairline bg-surface shadow-[0_24px_64px_rgba(0,0,0,0.45)]">
              <div className="sticky top-0 z-10 flex items-center justify-between gap-2 px-4 py-3 border-b border-hairline bg-surface/95 backdrop-blur-md">
                <div>
                  <div className="lt-label mb-0.5">Desk risk</div>
                  <div className="text-sm font-semibold text-ink">Limits & warnings</div>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={refreshAccount}
                    className="h-8 w-8 inline-flex items-center justify-center rounded-md border border-hairline text-muted hover:bg-elevated"
                    title="Refresh account"
                  >
                    <FontAwesomeIcon
                      icon={accountLoading ? faSpinner : faRotate}
                      className={`text-xs ${accountLoading ? 'animate-spin' : ''}`}
                    />
                  </button>
                  <button
                    type="button"
                    onClick={() => setOpen(false)}
                    className="h-8 w-8 inline-flex items-center justify-center rounded-md border border-hairline text-muted hover:bg-elevated"
                  >
                    <FontAwesomeIcon icon={faTimes} className="text-xs" />
                  </button>
                </div>
              </div>

            <div className="px-4 py-3 space-y-3">
              <div className="rounded-md border border-hairline bg-canvas p-3 grid grid-cols-2 gap-2">
                <div>
                  <div className="lt-label mb-1">Today P/L</div>
                  <div className={`num text-lg font-semibold ${dayPlTone}`}>
                    {dayPl == null
                      ? '—'
                      : `${dayPl >= 0 ? '+' : '-'}${formatMoney(Math.abs(dayPl))}`}
                  </div>
                </div>
                <div>
                  <div className="lt-label mb-1">Loss room</div>
                  <div
                    className={`num text-lg font-semibold ${
                      lossRoom == null
                        ? 'text-muted'
                        : lossRoom <= 0
                          ? 'text-bear'
                          : 'text-ink'
                    }`}
                  >
                    {lossRoom == null ? '—' : formatMoney(Math.max(0, lossRoom))}
                  </div>
                </div>
                <div>
                  <div className="lt-label mb-1">Buying power</div>
                  <div className="num text-sm text-ink">{formatMoney(account?.buyingPower)}</div>
                </div>
                <div>
                  <div className="lt-label mb-1">Day trades</div>
                  <div className="num text-sm text-ink">
                    {account?.dayTradeCount ?? '—'}
                    {account?.patternDayTrader ? (
                      <span className="ml-1 text-[9px] uppercase tracking-wider text-[var(--lt-warning)]">
                        PDT
                      </span>
                    ) : null}
                  </div>
                </div>
              </div>

              <label className="flex items-center justify-between gap-3 rounded-md border border-hairline bg-canvas px-3 py-2.5">
                <div>
                  <div className="text-xs font-semibold text-ink">Enforce risk checks</div>
                  <div className="text-[11px] text-muted">Block / warn before order submit</div>
                </div>
                <input
                  type="checkbox"
                  checked={Boolean(settings.enabled)}
                  onChange={(e) => updateSettings({ enabled: e.target.checked })}
                  className="h-4 w-4"
                />
              </label>

              <div className="space-y-2 rounded-md border border-hairline bg-canvas p-3">
                <div className="text-xs font-semibold text-ink">Hard limits</div>
                <div>
                  <label className="lt-label mb-1 block">Daily P/L loss limit (USD)</label>
                  <input
                    type="number"
                    min="0"
                    step="50"
                    value={settings.dailyLossLimitUsd ?? ''}
                    onChange={(e) =>
                      updateSettings({
                        dailyLossLimitUsd: e.target.value === '' ? null : Number(e.target.value),
                      })
                    }
                    className="w-full h-10 px-2.5 text-xs num lt-input"
                    placeholder="e.g. 500"
                    disabled={!settings.enabled}
                  />
                  <p className="text-[10px] text-muted mt-1">
                    Blocks new orders once today’s P/L ≤ −limit
                  </p>
                </div>
                <div>
                  <label className="lt-label mb-1 block">Max position notional (USD)</label>
                  <input
                    type="number"
                    min="0"
                    step="100"
                    value={settings.maxPositionNotionalUsd ?? ''}
                    onChange={(e) =>
                      updateSettings({
                        maxPositionNotionalUsd:
                          e.target.value === '' ? null : Number(e.target.value),
                      })
                    }
                    className="w-full h-10 px-2.5 text-xs num lt-input"
                    placeholder="e.g. 10000"
                    disabled={!settings.enabled}
                  />
                </div>
                <div>
                  <label className="lt-label mb-1 block">Max position quantity (shares)</label>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    value={settings.maxPositionQty ?? ''}
                    onChange={(e) =>
                      updateSettings({
                        maxPositionQty: e.target.value === '' ? null : Number(e.target.value),
                      })
                    }
                    className="w-full h-10 px-2.5 text-xs num lt-input"
                    placeholder="e.g. 100"
                    disabled={!settings.enabled}
                  />
                </div>
              </div>

              <div className="space-y-2 rounded-md border border-hairline bg-canvas p-3">
                <div className="text-xs font-semibold text-ink">Algo budget &amp; max loss</div>
                <p className="text-[10px] text-muted">
                  Also editable on the Algorithmic Trading page. Blocks new algo buys when hit; exits still allowed.
                </p>
                <div>
                  <label className="lt-label mb-1 block">Algo budget (USD)</label>
                  <input
                    type="number"
                    min="0"
                    step="100"
                    value={settings.algoBudgetUsd ?? ''}
                    onChange={(e) =>
                      updateSettings({
                        algoBudgetUsd: e.target.value === '' ? null : Number(e.target.value),
                      })
                    }
                    className="w-full h-10 px-2.5 text-xs num lt-input"
                    placeholder="e.g. 5000"
                    disabled={!settings.enabled}
                  />
                </div>
                <div className="flex gap-2">
                  <div className="flex-1">
                    <label className="lt-label mb-1 block">Max loss $</label>
                    <input
                      type="number"
                      min="0"
                      step="50"
                      value={settings.algoMaxLossUsd ?? ''}
                      onChange={(e) =>
                        updateSettings({
                          algoMaxLossMode: 'usd',
                          algoMaxLossUsd: e.target.value === '' ? null : Number(e.target.value),
                        })
                      }
                      className="w-full h-10 px-2.5 text-xs num lt-input"
                      placeholder="250"
                      disabled={!settings.enabled}
                    />
                  </div>
                  <div className="flex-1">
                    <label className="lt-label mb-1 block">or max loss %</label>
                    <input
                      type="number"
                      min="0"
                      step="0.5"
                      value={settings.algoMaxLossPct ?? ''}
                      onChange={(e) =>
                        updateSettings({
                          algoMaxLossMode: 'pct',
                          algoMaxLossPct: e.target.value === '' ? null : Number(e.target.value),
                        })
                      }
                      className="w-full h-10 px-2.5 text-xs num lt-input"
                      placeholder="2"
                      disabled={!settings.enabled}
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-2 rounded-md border border-hairline bg-canvas p-3">
                <div className="text-xs font-semibold text-ink">Pre-submit warnings</div>
                <label className="flex items-center justify-between gap-3">
                  <span className="text-xs text-ink">PDT / day-trade warnings</span>
                  <input
                    type="checkbox"
                    checked={Boolean(settings.warnOnPdt)}
                    onChange={(e) => updateSettings({ warnOnPdt: e.target.checked })}
                    className="h-4 w-4"
                    disabled={!settings.enabled}
                  />
                </label>
                <label className="flex items-center justify-between gap-3">
                  <span className="text-xs text-ink">Buying-power warnings</span>
                  <input
                    type="checkbox"
                    checked={Boolean(settings.warnOnBuyingPower)}
                    onChange={(e) => updateSettings({ warnOnBuyingPower: e.target.checked })}
                    className="h-4 w-4"
                    disabled={!settings.enabled}
                  />
                </label>
              </div>

              <p className="text-[10px] text-muted">
                Warnings require confirmation before submit. Hard limits block the order.
                Settings sync with your account preferences.
              </p>
            </div>
          </div>
          </div>,
          document.body
        )}
    </>
  );
};

export default RiskPanel;
